import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { 
  FileText, 
  Download, 
  Loader2, 
  Crown,
  BarChart3,
  Calendar,
  Lock,
  Mail,
  Send
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth, API } from "@/App";
import { toast } from "sonner";
import DashboardLayout from "@/components/DashboardLayout";
import { useNavigate } from "react-router-dom";

const ReportsPage = () => {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [downloadingReport, setDownloadingReport] = useState(null);
  const [emailingReport, setEmailingReport] = useState(false);

  const isPro = user?.email === "ai.manifest.official@gmail.com" || user?.subscription_tier === "pro" || user?.subscription_tier === "premium";

  const endpointMap = {
    activity: "/reports/activity",
    weekly: "/reports/weekly",
    monthly: "/reports/monthly-pdf",
    analytics: "/reports/analytics-pdf",
  };

  const handleDownloadReport = async (reportType) => {
    if (!isPro) {
      toast.error("PDF reports are a PRO feature. Please upgrade to access.");
      return;
    }

    setDownloadingReport(reportType);
    
    try {
      const endpoint = endpointMap[reportType] || `/reports/${reportType}`;
      const response = await axios.get(`${API}${endpoint}`, {
        responseType: "blob",
        withCredentials: true,
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      const filename = `AURA_${reportType.charAt(0).toUpperCase() + reportType.slice(1)}_Report_${new Date().toISOString().split('T')[0]}.pdf`;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success("Report downloaded successfully!");
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error("Please upgrade to PRO to download reports");
      } else {
        toast.error("Failed to download report");
      }
    } finally {
      setDownloadingReport(null);
    }
  };

  const handleEmailReport = async (reportType) => {
    if (!isPro) {
      toast.error("Email reports are a PRO feature. Please upgrade to access.");
      return;
    }

    setEmailingReport(true);
    try {
      await axios.post(
        `${API}/reports/email`,
        { report_type: reportType },
        {
          withCredentials: true,
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        }
      );
      toast.success(`Report sent to ${user?.email}!`);
    } catch (error) {
      toast.error("Failed to email report. Please try downloading instead.");
    } finally {
      setEmailingReport(false);
    }
  };

  const reports = [
    {
      id: "activity",
      title: "Activity Report",
      description: "Complete history of your games, winnings, and ROI with colour-coded matched numbers",
      icon: BarChart3,
      available: true,
      proOnly: true
    },
    {
      id: "weekly",
      title: "Weekly Report",
      description: "This week's games, predictions, winning entries, and net performance summary",
      icon: Calendar,
      available: true,
      proOnly: true
    },
    {
      id: "monthly",
      title: "Monthly Report",
      description: "30-day overview with spending, winnings, lottery breakdown, and monthly ROI",
      icon: FileText,
      available: true,
      proOnly: true
    }
  ];

  return (
    <DashboardLayout currentPage="reports">
      <div className="p-6 max-w-4xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-purple-500/10 border border-purple-500/20 text-purple-400 text-sm font-medium mb-6">
            <FileText className="w-4 h-4" />
            <span>PDF Export</span>
          </div>
          <h1 className="font-heading text-4xl font-bold text-white mb-4">
            Performance <span className="gradient-text">Reports</span>
          </h1>
          <p className="text-xl text-aura-text-secondary max-w-xl mx-auto">
            Download detailed PDF reports of your lottery activity and performance
          </p>
        </motion.div>

        {/* PRO Notice for free users */}
        {!isPro && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="card-aura p-6 mb-8 border-aura-gold/30 bg-gradient-to-r from-aura-gold/5 to-transparent"
          >
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-aura-gold/10 flex items-center justify-center">
                  <Crown className="w-6 h-6 text-aura-gold" />
                </div>
                <div>
                  <h3 className="font-heading text-lg font-semibold text-white">PRO Feature</h3>
                  <p className="text-aura-text-secondary text-sm">
                    Upgrade to AURA PRO to download PDF reports
                  </p>
                </div>
              </div>
              <Button
                className="btn-gold"
                onClick={() => navigate("/subscription")}
                data-testid="upgrade-btn"
              >
                <Crown className="w-4 h-4 mr-2" />
                Upgrade to PRO
              </Button>
            </div>
          </motion.div>
        )}

        {/* Report Cards */}
        <div className="grid md:grid-cols-2 gap-6">
          {reports.map((report, index) => (
            <motion.div
              key={report.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="card-aura p-6"
              data-testid={`report-card-${report.id}`}
            >
              <div className="flex items-start gap-4 mb-4">
                <div className="w-12 h-12 rounded-xl bg-aura-emerald/10 flex items-center justify-center">
                  <report.icon className="w-6 h-6 text-aura-emerald" />
                </div>
                {report.proOnly && (
                  <span className="ml-auto px-2 py-1 rounded text-xs bg-aura-gold/10 text-aura-gold border border-aura-gold/20">
                    PRO
                  </span>
                )}
              </div>
              
              <h3 className="font-heading text-lg font-semibold text-white mb-2">
                {report.title}
              </h3>
              <p className="text-sm text-aura-text-secondary mb-6">
                {report.description}
              </p>

              <div className="flex gap-3">
                <Button
                  className={`flex-1 ${isPro ? 'btn-primary' : 'bg-white/5 text-aura-text-secondary'}`}
                  onClick={() => handleDownloadReport(report.id)}
                  disabled={downloadingReport === report.id}
                  data-testid={`download-${report.id}-btn`}
                >
                  {downloadingReport === report.id ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Generating...
                    </>
                  ) : !isPro ? (
                    <>
                      <Lock className="w-4 h-4 mr-2" />
                      PRO Only
                    </>
                  ) : (
                    <>
                      <Download className="w-4 h-4 mr-2" />
                      Download
                    </>
                  )}
                </Button>
                
                {isPro && (
                  <Button
                    variant="outline"
                    className="border-aura-emerald/20 text-aura-emerald hover:bg-aura-emerald/10"
                    onClick={() => handleEmailReport(report.id)}
                    disabled={emailingReport}
                    data-testid={`email-${report.id}-btn`}
                  >
                    {emailingReport ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Send className="w-4 h-4" />
                    )}
                  </Button>
                )}
              </div>
            </motion.div>
          ))}
        </div>

        {/* Info Section */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
          className="mt-12 text-center"
        >
          <p className="text-sm text-aura-text-muted">
            Reports are branded PDF documents with AURA 8-ball logo, matched number highlighting, and responsible gambling information.
          </p>
        </motion.div>
      </div>
    </DashboardLayout>
  );
};

export default ReportsPage;
